/**
 * UNA PALABRA POR CONCEPTO
 * ========================
 *
 * Cómo se llama cada estado y cada evento en pantalla, en un solo lugar. El cartel del
 * mostrador, la fila de la lista, el filtro y el gráfico dicen lo mismo porque leen de acá.
 * Si un concepto tuviera dos nombres en dos pantallas, quien atiende pensaría que son dos
 * cosas distintas —y en el mostrador eso se paga dos veces—.
 */

export const NOMBRE_ESTADO_MOSTRADOR = {
  PENDIENTE: 'Hay que pagarle',
  PAGADO: 'Ya cobró',
  NO_INSCRIPTO: 'No figura en esta actividad',
  SIN_REGISTRO: 'No está registrado',
} as const;

export const NOMBRE_ESTADO_ACTIVIDAD = {
  BORRADOR: 'Borrador',
  ABIERTA: 'Abierta',
  CERRADA: 'Cerrada',
  ANULADA: 'Anulada',
} as const;

export const NOMBRE_ESTADO_PARTICIPACION = {
  PENDIENTE: 'Pendiente',
  PAGADO: 'Pagado',
} as const;

export const NOMBRE_ORIGEN_DATOS = {
  MANUAL: 'Cargado a mano',
  PADRON_DNIT: 'Propuesto por el padrón',
  IMPORTADO_PLANILLA: 'Importado en lote',
} as const;

/**
 * EVENTOS DE NEGOCIO
 *
 * El trigger de la base audita cada cambio de fila; eso atrapa todo y es ilegible. Estos son
 * los hechos que una persona busca cuando pregunta qué pasó, y los emite la aplicación con
 * nombre propio además del diff técnico.
 */
export const EVENTOS = {
  PAGO_REGISTRADO: 'PAGO_REGISTRADO',
  PAGO_ANULADO: 'PAGO_ANULADO',
  MONTO_AJUSTADO: 'MONTO_AJUSTADO',
  ACTIVIDAD_ABIERTA: 'ACTIVIDAD_ABIERTA',
  ACTIVIDAD_CERRADA: 'ACTIVIDAD_CERRADA',
  ACTIVIDAD_ANULADA: 'ACTIVIDAD_ANULADA',
  INTEGRANTE_DADO_DE_BAJA: 'INTEGRANTE_DADO_DE_BAJA',
  ALTA_EN_MOSTRADOR: 'ALTA_EN_MOSTRADOR',
  INSCRIPCION_EN_LOTE: 'INSCRIPCION_EN_LOTE',
  PERSONA_AGREGADA: 'PERSONA_AGREGADA',
  PARTICIPACION_QUITADA: 'PARTICIPACION_QUITADA',
  INTEGRANTES_IMPORTADOS: 'INTEGRANTES_IMPORTADOS',
  ACCESO_SOPORTE: 'ACCESO_SOPORTE',
  INGRESO: 'INGRESO',
  CAMBIO_DE_ENTIDAD: 'CAMBIO_DE_ENTIDAD',
  PERMISOS_CAMBIADOS: 'PERMISOS_CAMBIADOS',
  ROL_CREADO: 'ROL_CREADO',
  MIEMBRO_AGREGADO: 'MIEMBRO_AGREGADO',
  MIEMBRO_DADO_DE_BAJA: 'MIEMBRO_DADO_DE_BAJA',
  CLAVE_RESTABLECIDA: 'CLAVE_RESTABLECIDA',
  ENTIDAD_CREADA: 'ENTIDAD_CREADA',
  ENTIDAD_ESTADO: 'ENTIDAD_ESTADO',
  MODULO_CAMBIADO: 'MODULO_CAMBIADO',
  ZONA_HORARIA_CAMBIADA: 'ZONA_HORARIA_CAMBIADA',
} as const;

export type ClaveEvento = (typeof EVENTOS)[keyof typeof EVENTOS];

/** Cómo se lee cada evento en la pantalla de auditoría. */
export const NOMBRE_EVENTO: Record<ClaveEvento, string> = {
  PAGO_REGISTRADO: 'Pago registrado',
  PAGO_ANULADO: 'Pago anulado',
  MONTO_AJUSTADO: 'Monto ajustado',
  ACTIVIDAD_ABIERTA: 'Actividad abierta',
  ACTIVIDAD_CERRADA: 'Actividad cerrada',
  ACTIVIDAD_ANULADA: 'Actividad anulada',
  INTEGRANTE_DADO_DE_BAJA: 'Integrante dado de baja',
  ALTA_EN_MOSTRADOR: 'Alta en el mostrador',
  INSCRIPCION_EN_LOTE: 'Inscripción en lote',
  PERSONA_AGREGADA: 'Persona agregada a una actividad',
  PARTICIPACION_QUITADA: 'Participación quitada',
  INTEGRANTES_IMPORTADOS: 'Integrantes importados',
  ACCESO_SOPORTE: 'Acceso de soporte de RASP',
  INGRESO: 'Ingreso al sistema',
  CAMBIO_DE_ENTIDAD: 'Cambio de entidad',
  PERMISOS_CAMBIADOS: 'Permisos cambiados',
  ROL_CREADO: 'Rol creado',
  MIEMBRO_AGREGADO: 'Miembro agregado',
  MIEMBRO_DADO_DE_BAJA: 'Miembro dado de baja',
  CLAVE_RESTABLECIDA: 'Contraseña restablecida',
  ENTIDAD_CREADA: 'Entidad creada',
  ENTIDAD_ESTADO: 'Estado de la entidad',
  MODULO_CAMBIADO: 'Módulo habilitado o deshabilitado',
  ZONA_HORARIA_CAMBIADA: 'Zona horaria cambiada',
};

/** Cómo se nombra cada tabla en la auditoría, para no mostrarle `modulo_entidad` a nadie. */
export const NOMBRE_TABLA = {
  entidad: 'Entidad',
  plan: 'Plan',
  modulo_entidad: 'Módulo de la entidad',
  usuario: 'Usuario',
  membresia: 'Miembro',
  rol: 'Rol',
  archivo: 'Archivo',
  zona: 'Zona',
  integrante: 'Integrante',
  tipo_actividad: 'Tipo de actividad',
  actividad: 'Actividad',
  participacion: 'Participación',
  medio_pago: 'Medio de pago',
  pago: 'Pago',
} as const;
